import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Pencil, Trash2, ChevronUp, ChevronDown, Clock, Copy, Archive, ArchiveRestore, ListChecks, Sparkles } from "lucide-react";
import { Layout } from "../../components/layout/Layout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { Field, EmptyState, ConfirmDialog } from "../../components/common";
import { DurationInput } from "../../components/DurationInput";
import { ModuleFormDialog } from "./ModuleListPage";
import { api, formatApiError } from "../../lib/api";
import { fmtDuration } from "../../lib/format";
import { NOTES, NoteBadge, ALL_NOTE_IDS } from "../../lib/notes";
import { useAuth, hasPerm } from "../../context/AuthContext";
import { cn } from "../../lib/utils";

function ActivityDialog({ open, onOpenChange, moduleId, initial, onSaved }) {
  const [nom, setNom] = useState("");
  const [description, setDescription] = useState("");
  const [duree, setDuree] = useState(null);
  const [notes, setNotes] = useState(ALL_NOTE_IDS);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setNom(initial?.nom || "");
    setDescription(initial?.description || "");
    setDuree(initial?.duree_minutes ?? null);
    setNotes(initial?.notes_possibles || ALL_NOTE_IDS);
  }, [open, initial]);

  const toggleNote = (id) => setNotes((n) => (n.includes(id) ? n.filter((x) => x !== id) : [...n, id]));

  const submit = async (e) => {
    e.preventDefault();
    if (!notes.length) return toast.error("Sélectionnez au moins une note possible");
    setSaving(true);
    try {
      const payload = { nom, description, duree_minutes: duree, notes_possibles: notes };
      const { data } = initial
        ? await api.patch(`/modules/${moduleId}/activities/${initial.id}`, payload)
        : await api.post(`/modules/${moduleId}/activities`, payload);
      toast.success(initial ? "Activité modifiée" : "Activité ajoutée");
      onOpenChange(false);
      onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initial ? "Modifier l'activité" : "Nouvelle activité"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="activity-form">
          <Field label="Nom de l'activité" required>
            <Input value={nom} onChange={(e) => setNom(e.target.value)} required autoFocus placeholder="Décollage de l'écran" data-testid="activity-form-nom" />
          </Field>
          <Field label="Description">
            <Textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Étapes, points de vigilance..." />
          </Field>
          <Field label="Temps (facultatif)" hint="Le temps du module se met à jour automatiquement avec la somme des temps des activités.">
            <DurationInput value={duree} onChange={setDuree} idPrefix="activity-form-duree" />
          </Field>
          <div>
            <p className="text-sm font-medium mb-1">Gérer les notes</p>
            <p className="text-xs text-muted-foreground mb-2">Cochez les notes que le formateur pourra attribuer pour cette activité.</p>
            <div className="space-y-1.5">
              {NOTES.map((n) => (
                <label key={n.id} className={cn("flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer transition-colors", notes.includes(n.id) ? "border-primary/40 bg-primary/5" : "border-border hover:bg-accent/50")}>
                  <input type="checkbox" checked={notes.includes(n.id)} onChange={() => toggleNote(n.id)} className="h-4 w-4 accent-[hsl(var(--primary))]" data-testid={`activity-note-${n.id}`} />
                  <NoteBadge note={n.id} />
                </label>
              ))}
            </div>
            <div className="flex gap-3 mt-2 text-xs">
              <button type="button" className="text-primary hover:underline" onClick={() => setNotes(ALL_NOTE_IDS)}>Toutes</button>
              <button type="button" className="text-primary hover:underline" onClick={() => setNotes(["echoue", "realise"])}>Échoué / Réalisé seulement</button>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={saving} data-testid="activity-form-submit">{initial ? "Enregistrer" : "Créer l'activité"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function ModuleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canManage = hasPerm(user, "modules", "manage");
  const [module, setModule] = useState(null);
  const [error, setError] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [actOpen, setActOpen] = useState(false);
  const [actEdit, setActEdit] = useState(null);
  const [actDelete, setActDelete] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = () => api.get(`/modules/${id}`).then(({ data }) => setModule(data)).catch((e) => setError(formatApiError(e.response?.data?.detail)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  if (error) return <Layout title="Module"><p className="text-destructive">{error}</p></Layout>;
  if (!module) return <Layout title="Module"><p className="text-muted-foreground">Chargement...</p></Layout>;

  const acts = module.activities || [];
  const move = async (index, delta) => {
    const order = acts.map((a) => a.id);
    const [x] = order.splice(index, 1);
    order.splice(index + delta, 0, x);
    try {
      const { data } = await api.post(`/modules/${id}/activities/reorder`, { order });
      setModule(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const deleteActivity = async () => {
    try {
      const { data } = await api.delete(`/modules/${id}/activities/${actDelete.id}`);
      setModule(data);
      toast.success("Activité supprimée");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setActDelete(null);
    }
  };
  const archive = async () => {
    const { data } = await api.patch(`/modules/${id}`, { archived: !module.archived });
    setModule(data);
    toast.success(data.archived ? "Module archivé (il n'apparaît plus dans les listes de choix)" : "Module réactivé");
  };
  const duplicate = async () => {
    const { data } = await api.post(`/modules/${id}/duplicate`);
    toast.success("Module dupliqué");
    navigate(`/modules/${data.id}`);
  };
  const remove = async () => {
    try {
      await api.delete(`/modules/${id}`);
      toast.success("Module supprimé");
      navigate("/modules");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setConfirmDelete(false);
    }
  };

  const timedCount = acts.filter((a) => a.duree_minutes).length;

  return (
    <Layout title={module.nom}>
      <Link to="/modules" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft size={15} /> Tous les modules</Link>

      <div className="grid gap-5 lg:grid-cols-[1fr_300px]">
        <div className="space-y-5 min-w-0">
          <section className="rounded-xl border border-border bg-card p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="font-heading text-xl font-semibold">{module.nom}</h2>
                {module.archived && <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Archive size={12} /> Module archivé</p>}
              </div>
              {canManage && <Button variant="outline" size="sm" onClick={() => setEditOpen(true)} data-testid="module-edit-button"><Pencil size={14} /> Modifier</Button>}
            </div>
            <p className="text-sm text-muted-foreground mt-2 whitespace-pre-line">{module.description || "Pas de description."}</p>
          </section>

          <section className="rounded-xl border border-border bg-card">
            <div className="flex items-center justify-between gap-3 px-5 py-3.5 border-b border-border">
              <h2 className="font-heading font-semibold flex items-center gap-2"><ListChecks size={17} className="text-muted-foreground" /> Activités <span className="text-muted-foreground font-normal text-sm">({acts.length})</span></h2>
              {canManage && <Button size="sm" onClick={() => { setActEdit(null); setActOpen(true); }} data-testid="activity-add-button"><Plus size={14} /> Ajouter une activité</Button>}
            </div>
            {acts.length === 0 ? (
              <EmptyState icon={ListChecks} title="Aucune activité" text="Exemple pour un remplacement d'écran : décollage de l'écran, dessoudage de nappe, nettoyage interne, changement du joint, recollage." />
            ) : (
              <ol className="divide-y divide-border">
                {acts.map((a, i) => (
                  <li key={a.id} className="flex gap-3 px-4 sm:px-5 py-3.5" data-testid={`activity-row-${a.id}`}>
                    <span className="mt-0.5 h-6 w-6 shrink-0 rounded-full bg-muted text-xs font-semibold flex items-center justify-center tabular-nums">{i + 1}</span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <p className="font-medium">{a.nom}</p>
                        {a.duree_minutes ? <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock size={12} />{fmtDuration(a.duree_minutes)}</span> : null}
                      </div>
                      {a.description && <p className="text-sm text-muted-foreground mt-0.5 whitespace-pre-line">{a.description}</p>}
                      <div className="flex flex-wrap gap-1 mt-2">
                        {(a.notes_possibles || []).map((n) => <NoteBadge key={n} note={n} short />)}
                      </div>
                    </div>
                    {canManage && (
                      <div className="flex items-start gap-0.5 shrink-0">
                        <Button variant="ghost" size="icon" className="h-8 w-8" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Monter"><ChevronUp size={16} /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" disabled={i === acts.length - 1} onClick={() => move(i, 1)} aria-label="Descendre"><ChevronDown size={16} /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setActEdit(a); setActOpen(true); }} aria-label="Modifier"><Pencil size={15} /></Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setActDelete(a)} aria-label="Supprimer"><Trash2 size={15} /></Button>
                      </div>
                    )}
                  </li>
                ))}
              </ol>
            )}
          </section>
        </div>

        <aside className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-5">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Durée du module</p>
            <p className="font-heading text-3xl font-semibold mt-1 tabular-nums" data-testid="module-duree">{fmtDuration(module.duree_minutes)}</p>
            <p className="text-xs text-muted-foreground mt-1.5 flex items-start gap-1">
              {module.duree_auto ? <><Sparkles size={12} className="mt-0.5 shrink-0" /> Somme des temps de {timedCount} activité{timedCount > 1 ? "s" : ""}</> : module.duree_minutes ? "Durée saisie sur le module" : "Aucune durée configurée"}
            </p>
            <p className="text-xs text-muted-foreground mt-3">Utilisée pour remplir automatiquement l'heure de fin d'un cours dans le planning.</p>
          </div>
          {canManage && (
            <div className="rounded-xl border border-border bg-card p-3 space-y-1">
              <Button variant="ghost" className="w-full justify-start" onClick={duplicate}><Copy size={15} /> Dupliquer le module</Button>
              <Button variant="ghost" className="w-full justify-start" onClick={archive}>{module.archived ? <><ArchiveRestore size={15} /> Réactiver</> : <><Archive size={15} /> Archiver</>}</Button>
              <Button variant="ghost" className="w-full justify-start text-destructive hover:text-destructive" onClick={() => setConfirmDelete(true)}><Trash2 size={15} /> Supprimer</Button>
            </div>
          )}
        </aside>
      </div>

      <ModuleFormDialog open={editOpen} onOpenChange={setEditOpen} initial={module} onSaved={setModule} />
      <ActivityDialog open={actOpen} onOpenChange={setActOpen} moduleId={id} initial={actEdit} onSaved={setModule} />
      <ConfirmDialog open={!!actDelete} onOpenChange={(o) => !o && setActDelete(null)} title="Supprimer cette activité ?" description={actDelete ? `« ${actDelete.nom} » disparaîtra du module et des fiches élèves qui le contiennent.` : ""} confirmLabel="Supprimer" onConfirm={deleteActivity} />
      <ConfirmDialog open={confirmDelete} onOpenChange={setConfirmDelete} title="Supprimer ce module ?" description="Impossible si le module est déjà utilisé sur une fiche élève ou dans le planning (archivez-le dans ce cas)." confirmLabel="Supprimer" onConfirm={remove} />
    </Layout>
  );
}
